/**
 * AudioSourceInstance
 *
 * Represents a single playing audio instance (a "voice").
 * Wraps AudioBufferSourceNode with volume, pitch, spatial, fade, and bus routing.
 *
 * @module @engine/audio/AudioSourceInstance
 */

import type { AudioPlaybackOptions, AudioFadeOptions, SpatialAudioConfig, AudioSourceState } from './types';

let nextInstanceId = 0;

export class AudioSourceInstance {
  readonly id: number;
  readonly clipGuid: string;
  readonly busId: string;
  readonly priority: number;
  readonly createdAt: number;

  private _ctx: AudioContext;
  private _sourceNode: AudioBufferSourceNode | null = null;
  private _gainNode: GainNode;
  private _pannerNode: PannerNode | null = null;
  private _output: AudioNode;
  private _startTime: number = 0;
  private _startOffset: number = 0;

  private _volume: number = 1;
  private _pitch: number = 1;
  private _loop: boolean = false;
  private _playing: boolean = false;
  private _paused: boolean = false;
  private _pauseTime: number = 0;
  private _fadeTimeout: ReturnType<typeof setTimeout> | null = null;

  // Spatial config
  private _spatialConfig: SpatialAudioConfig | null = null;

  // Velocity tracking for doppler
  private _curPosX: number = 0;
  private _curPosY: number = 0;
  private _curPosZ: number = 0;
  private _velX: number = 0;
  private _velY: number = 0;
  private _velZ: number = 0;
  private _hasPrevPosition: boolean = false;
  private _dopplerPitch: number = 1;

  // Callbacks
  onEnded: (() => void) | null = null;

  constructor(
    ctx: AudioContext,
    clipGuid: string,
    busInput: AudioNode,
    options: AudioPlaybackOptions = {},
  ) {
    this.id = nextInstanceId++;
    this.clipGuid = clipGuid;
    this.busId = options.busId ?? 'master';
    this.priority = options.priority ?? 128;
    this.createdAt = ctx.currentTime;

    this._ctx = ctx;
    this._volume = options.volume ?? 1;
    this._pitch = options.pitch ?? 1;
    this._loop = options.loop ?? false;
    this._startOffset = options.startTime ?? 0;

    // Create gain node
    this._gainNode = ctx.createGain();
    this._gainNode.gain.setValueAtTime(this._volume, ctx.currentTime);

    // Create panner if spatial
    if (options.spatial?.enabled || options.position) {
      this._setupSpatial(options);
    }

    // Connect: source -> [panner ->] gain -> busInput
    if (this._pannerNode) {
      this._pannerNode.connect(this._gainNode);
    }
    this._gainNode.connect(busInput);
    this._output = busInput;
  }

  // ==========================================================================
  // PUBLIC API
  // ==========================================================================

  get playing(): boolean { return this._playing; }
  get paused(): boolean { return this._paused; }
  get loop(): boolean { return this._loop; }
  set loop(v: boolean) {
    this._loop = v;
    if (this._sourceNode) this._sourceNode.loop = v;
  }

  get volume(): number { return this._volume; }
  set volume(v: number) {
    this._volume = Math.max(0, v);
    this._gainNode.gain.setTargetAtTime(this._volume, this._ctx.currentTime, 0.02);
  }

  get pitch(): number { return this._pitch; }
  set pitch(v: number) {
    this._pitch = Math.max(0.01, v);
    this._applyEffectivePitch();
  }

  /** Doppler-induced pitch multiplier (set by the engine each frame). */
  get dopplerPitch(): number { return this._dopplerPitch; }
  set dopplerPitch(v: number) {
    this._dopplerPitch = v;
    this._applyEffectivePitch();
  }

  /** Whether this voice has a PannerNode (spatial audio). */
  get isSpatial(): boolean { return this._pannerNode !== null; }

  /** Per-source doppler factor from spatial config. */
  get spatialDopplerFactor(): number { return this._spatialConfig?.dopplerFactor ?? 0; }

  /** Current tracked position X. */
  get positionX(): number { return this._curPosX; }
  /** Current tracked position Y. */
  get positionY(): number { return this._curPosY; }
  /** Current tracked position Z. */
  get positionZ(): number { return this._curPosZ; }

  /** Current velocity X (units/sec). */
  get velocityX(): number { return this._velX; }
  /** Current velocity Y (units/sec). */
  get velocityY(): number { return this._velY; }
  /** Current velocity Z (units/sec). */
  get velocityZ(): number { return this._velZ; }

  /** Current playback time in seconds */
  get currentTime(): number {
    if (this._paused) return this._pauseTime;
    if (!this._playing) return 0;
    return (this._ctx.currentTime - this._startTime) * this._pitch + this._startOffset;
  }

  /** Get full state */
  getState(): AudioSourceState {
    return {
      playing: this._playing,
      paused: this._paused,
      currentTime: this.currentTime,
      duration: this._sourceNode?.buffer?.duration ?? 0,
      volume: this._volume,
      pitch: this._pitch,
      loop: this._loop,
    };
  }

  /**
   * Start playback.
   */
  play(buffer: AudioBuffer, delay: number = 0): void {
    if (this._playing && !this._paused) {
      this.stop();
    }

    this._sourceNode = this._ctx.createBufferSource();
    this._sourceNode.buffer = buffer;
    this._sourceNode.loop = this._loop;
    this._sourceNode.playbackRate.setValueAtTime(this._pitch * this._dopplerPitch, this._ctx.currentTime);

    // Connect source
    if (this._pannerNode) {
      this._sourceNode.connect(this._pannerNode);
    } else {
      this._sourceNode.connect(this._gainNode);
    }

    this._sourceNode.onended = () => {
      if (!this._paused) {
        this._playing = false;
        this.onEnded?.();
      }
    };

    const when = this._ctx.currentTime + delay;
    this._sourceNode.start(when, this._startOffset);
    this._startTime = when;
    this._playing = true;
    this._paused = false;
  }

  /**
   * Stop playback.
   */
  stop(): void {
    if (this._sourceNode) {
      try {
        this._sourceNode.onended = null;
        this._sourceNode.stop();
        this._sourceNode.disconnect();
      } catch { /* already stopped */ }
      this._sourceNode = null;
    }
    this._playing = false;
    this._paused = false;
    this._startOffset = 0;
    this._clearFade();
  }

  /**
   * Pause playback (can resume).
   */
  pause(): void {
    if (!this._playing || this._paused) return;
    this._pauseTime = this.currentTime;
    if (this._sourceNode) {
      try {
        this._sourceNode.onended = null;
        this._sourceNode.stop();
        this._sourceNode.disconnect();
      } catch { /* already stopped */ }
      this._sourceNode = null;
    }
    this._paused = true;
  }

  /**
   * Resume after pause.
   */
  resume(buffer: AudioBuffer): void {
    if (!this._paused) return;
    this._startOffset = this._pauseTime;
    this._paused = false;
    this.play(buffer);
  }

  /**
   * Fade volume over time.
   */
  fade(options: AudioFadeOptions): void {
    this._clearFade();
    const target = options.targetVolume ?? 0;
    this._gainNode.gain.linearRampToValueAtTime(
      target,
      this._ctx.currentTime + options.duration,
    );

    if (options.stopAfterFade) {
      this._fadeTimeout = setTimeout(() => {
        this.stop();
      }, options.duration * 1000 + 50);
    }
  }

  /**
   * Update spatial position.
   */
  setPosition(x: number, y: number, z: number = 0): void {
    if (!this._pannerNode) return;
    this._pannerNode.positionX.setValueAtTime(x, this._ctx.currentTime);
    this._pannerNode.positionY.setValueAtTime(y, this._ctx.currentTime);
    this._pannerNode.positionZ.setValueAtTime(z, this._ctx.currentTime);
    this._curPosX = x;
    this._curPosY = y;
    this._curPosZ = z;
  }

  /**
   * Update position with velocity tracking for doppler effect.
   * Computes velocity from position delta and sets the new position.
   */
  updatePositionTracked(x: number, y: number, z: number, deltaTime: number): void {
    if (this._hasPrevPosition && deltaTime > 0) {
      this._velX = (x - this._curPosX) / deltaTime;
      this._velY = (y - this._curPosY) / deltaTime;
      this._velZ = (z - this._curPosZ) / deltaTime;
    }
    this.setPosition(x, y, z);
    this._hasPrevPosition = true;
  }

  /**
   * Update spatial orientation.
   */
  setOrientation(fx: number, fy: number, fz: number): void {
    if (!this._pannerNode) return;
    this._pannerNode.orientationX.setValueAtTime(fx, this._ctx.currentTime);
    this._pannerNode.orientationY.setValueAtTime(fy, this._ctx.currentTime);
    this._pannerNode.orientationZ.setValueAtTime(fz, this._ctx.currentTime);
  }

  /**
   * Update spatial config at runtime.
   */
  updateSpatialConfig(config: Partial<SpatialAudioConfig>): void {
    if (!this._pannerNode) return;
    if (config.distanceModel !== undefined) this._pannerNode.distanceModel = config.distanceModel;
    if (config.panningModel !== undefined) this._pannerNode.panningModel = config.panningModel;
    if (config.refDistance !== undefined) this._pannerNode.refDistance = config.refDistance;
    if (config.maxDistance !== undefined) this._pannerNode.maxDistance = config.maxDistance;
    if (config.rolloffFactor !== undefined) this._pannerNode.rolloffFactor = config.rolloffFactor;
    if (config.coneInnerAngle !== undefined) this._pannerNode.coneInnerAngle = config.coneInnerAngle;
    if (config.coneOuterAngle !== undefined) this._pannerNode.coneOuterAngle = config.coneOuterAngle;
    if (config.coneOuterGain !== undefined) this._pannerNode.coneOuterGain = config.coneOuterGain;
  }

  /**
   * Destroy and release all resources.
   */
  destroy(): void {
    this.stop();
    try {
      this._gainNode.disconnect();
      this._pannerNode?.disconnect();
    } catch { /* no-op */ }
  }

  // ==========================================================================
  // PRIVATE
  // ==========================================================================

  private _setupSpatial(options: AudioPlaybackOptions): void {
    const spatial = options.spatial ?? {};
    this._pannerNode = this._ctx.createPanner();

    this._pannerNode.panningModel = spatial.panningModel ?? 'HRTF';
    this._pannerNode.distanceModel = spatial.distanceModel ?? 'inverse';
    this._pannerNode.refDistance = spatial.refDistance ?? 1;
    this._pannerNode.maxDistance = spatial.maxDistance ?? 100;
    this._pannerNode.rolloffFactor = spatial.rolloffFactor ?? 1;
    this._pannerNode.coneInnerAngle = spatial.coneInnerAngle ?? 360;
    this._pannerNode.coneOuterAngle = spatial.coneOuterAngle ?? 360;
    this._pannerNode.coneOuterGain = spatial.coneOuterGain ?? 0;

    if (options.position) {
      this._pannerNode.positionX.setValueAtTime(options.position.x, this._ctx.currentTime);
      this._pannerNode.positionY.setValueAtTime(options.position.y, this._ctx.currentTime);
      this._pannerNode.positionZ.setValueAtTime(options.position.z, this._ctx.currentTime);
      this._curPosX = options.position.x;
      this._curPosY = options.position.y;
      this._curPosZ = options.position.z;
    }

    this._spatialConfig = {
      enabled: true,
      panningModel: this._pannerNode.panningModel as 'HRTF' | 'equalpower',
      distanceModel: this._pannerNode.distanceModel,
      refDistance: this._pannerNode.refDistance,
      maxDistance: this._pannerNode.maxDistance,
      rolloffFactor: this._pannerNode.rolloffFactor,
      coneInnerAngle: this._pannerNode.coneInnerAngle,
      coneOuterAngle: this._pannerNode.coneOuterAngle,
      coneOuterGain: this._pannerNode.coneOuterGain,
      dopplerFactor: spatial.dopplerFactor ?? 0,
    };
  }

  /** Apply the effective pitch (base pitch × doppler pitch) to the source node. */
  private _applyEffectivePitch(): void {
    if (this._sourceNode) {
      this._sourceNode.playbackRate.setTargetAtTime(
        Math.max(0.01, this._pitch * this._dopplerPitch),
        this._ctx.currentTime, 0.02,
      );
    }
  }

  private _clearFade(): void {
    if (this._fadeTimeout) {
      clearTimeout(this._fadeTimeout);
      this._fadeTimeout = null;
    }
  }
}
